import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    popovers: [], // Array of popovers: [{ isVisible, contentId, position, targetId, props }, ...]
    // Keep legacy single popover state for backward compatibility
    isVisible: false,
    contentId: null,
    position: { top: 0, left: 0 },
    targetId: null,
    props: {},
};

const popoverSlice = createSlice({
    name: 'popover',
    initialState,
    reducers: {
        togglePopover: (state, action) => {
            const { contentId, position, targetId, props } = action.payload;

            // If contentId is null, it's a close request (from close button or outside click)
            if (contentId === null) {
                // Remove the most recent non-pinned popover or the one being closed
                const lastPopoverIndex = state.popovers.length - 1;
                if (lastPopoverIndex >= 0) {
                    state.popovers.splice(lastPopoverIndex, 1);
                }

                // Update legacy state to the last remaining popover or reset
                if (state.popovers.length > 0) {
                    const lastPopover = state.popovers[state.popovers.length - 1];
                    state.isVisible = lastPopover.isVisible;
                    state.contentId = lastPopover.contentId;
                    state.position = lastPopover.position;
                    state.targetId = lastPopover.targetId;
                    state.props = lastPopover.props;
                } else {
                    state.isVisible = false;
                    state.contentId = null;
                    state.position = { top: 0, left: 0 };
                    state.targetId = null;
                    state.props = {};
                }
                return;
            }

            // Check if this popover is already open (toggle behavior)
            const existingIndex = state.popovers.findIndex(
                p => p.contentId === contentId && p.targetId === targetId
            );

            if (existingIndex !== -1) {
                // Popover exists - check if it's pinned
                const existingPopover = state.popovers[existingIndex];
                if (existingPopover.props?.pinned === true) {
                    return; // Don't close pinned popover
                }

                // Remove the non-pinned popover
                state.popovers.splice(existingIndex, 1);
            } else {
                // Opening a new popover - close all non-pinned popovers first
                state.popovers = state.popovers.filter(p => p.props?.pinned === true);

                // Open the new popover
                const newPopover = {
                    isVisible: true,
                    contentId,
                    position: props?.isPopPositionChanged === false && state.position
                        ? state.position
                        : position,
                    targetId,
                    props: props || {},
                };

                state.popovers.push(newPopover);
            }

            // Update legacy state to the last popover
            if (state.popovers.length > 0) {
                const lastPopover = state.popovers[state.popovers.length - 1];
                state.isVisible = lastPopover.isVisible;
                state.contentId = lastPopover.contentId;
                state.position = lastPopover.position;
                state.targetId = lastPopover.targetId;
                state.props = lastPopover.props;
            } else {
                state.isVisible = false;
                state.contentId = null;
                state.position = { top: 0, left: 0 };
                state.targetId = null;
                state.props = {};
            }
        },
        updatePopoverPosition: (state, action) => {
            state.position = action.payload;
            // Also update the last popover in the array
            if (state.popovers.length > 0) {
                state.popovers[state.popovers.length - 1].position = action.payload;
            }
        },
        updatePopoverProps: (state, action) => {
            const { contentId, targetId, props } = action.payload;
            
            // Find the popover by contentId and targetId
            const popoverIndex = state.popovers.findIndex(
                p => p.contentId === contentId && p.targetId === targetId
            );
            
            if (popoverIndex !== -1) {
                // Update the props of the existing popover
                state.popovers[popoverIndex].props = {
                    ...state.popovers[popoverIndex].props,
                    ...props
                };
                
                // Update legacy state if this is the last popover
                if (popoverIndex === state.popovers.length - 1) {
                    state.props = state.popovers[popoverIndex].props;
                }
            }
        },
    },
});


export const { togglePopover, updatePopoverPosition, updatePopoverProps } = popoverSlice.actions;
export default popoverSlice.reducer;
